'use es6';

import PropTypes from 'prop-types';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getName, getId as getReportId } from '../../../data/schemas/reportSchema';
import { useHideDialog } from '../../../dialog/ducks';
import { useMutation } from 'data-fetching-client';
import { DELETE_REPORT } from '../../../data/requests/report';
import { getDefaultSavedReportActionEventProperties } from '../../report-action-wrapper';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { useUserRoleForReport } from '../../../utils/trackingUtils';
import ParentDashboardList from '../../shared/ParentDashboardList';
import { getReportDashboardInfo } from '../../../data/schemas/reportSchema';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Set as ImmutableSet } from 'immutable';
import I18n from 'I18n';
import { FETCH_REPORT_COUNTS } from '../../../data/requests/reportCounts';
import { useHasSomeCommentingAccessScopes } from '../../../access/scopes/hooks/useHasScope';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DeleteReportModal = ({
  appUsageTracker,
  reports,
  onDeleteReportSuccess = noop,
  filteredReportCount = 0,
  filters
}) => {
  const hideDialog = useHideDialog();
  const firstReport = !reports.isEmpty() && reports.first();
  const usageTracker = useTracker(appUsageTracker);
  const isDeleteAll = Boolean(reports.isEmpty() && filteredReportCount);
  const count = isDeleteAll ? filteredReportCount : reports.size;
  const isMultiDelete = count > 1;
  const reportName = firstReport ? getName(firstReport) : null;
  const userRole = useUserRoleForReport(firstReport);

  // Leverage scope checks to guarantee that comment feature specific copy only
  // renders for portals that truly have access to the feature.
  const hasSomeCommentingAccessScopes = useHasSomeCommentingAccessScopes();
  const reportIds = reports.reduce((memo, report) => memo.add(getReportId(report)), ImmutableSet()).toJS();
  const showParentDashboardsList = reports.some(report => getReportDashboardInfo(report).size > 0);
  const [deleteReportMutation] = useMutation(DELETE_REPORT, {
    variables: {
      reportIds,
      filters
    },
    onCompleted: () => {
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "reporting-action-components.actions.delete.modal.success",
          options: {
            reportName,
            count
          }
        }),
        type: 'success'
      });
      onDeleteReportSuccess();
      hideDialog();
    },
    onError: () => {
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.delete.modal.failure",
          options: {
            count
          }
        }),
        type: 'danger'
      });
    },
    refetchQueries: () => {
      return [{
        query: FETCH_REPORT_COUNTS
      }];
    }
  });
  const handleConfirm = () => {
    if (usageTracker) {
      const baseDeleteAssetProperties = {
        actionType: 'delete',
        action: 'delete',
        assetType: 'report',
        count
      };
      const deleteAssetProperties = isMultiDelete ? baseDeleteAssetProperties : Object.assign({}, baseDeleteAssetProperties, getDefaultSavedReportActionEventProperties(firstReport), {
        role: userRole
      });
      usageTracker.track(EVENTS.deleteAsset, deleteAssetProperties);
    }
    deleteReportMutation({
      variables: {
        reportIds,
        filters,
        deleteAll: isDeleteAll
      }
    });
  };
  return /*#__PURE__*/_jsx(UIConfirmModal, {
    "data-test-id": "report-delete-modal",
    confirmUse: "danger",
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.delete.modal.message",
      options: {
        count,
        reportName
      }
    }),
    width: 550,
    description: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: hasSomeCommentingAccessScopes ? 'reporting-action-components.actions.delete.modal.updatedDescription' : 'reporting-action-components.actions.delete.modal.description',
        options: {
          count
        }
      }), showParentDashboardsList && /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.delete.modal.dashboardListLabel",
          options: {
            count
          }
        }), /*#__PURE__*/_jsx(ParentDashboardList, {
          reports: reports,
          formatAsTable: true
        })]
      })]
    }),
    confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.delete.modal.confirm",
      options: {
        count
      }
    }),
    onConfirm: handleConfirm,
    rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.delete.modal.reject"
    }),
    onReject: hideDialog,
    use: "danger",
    match: isMultiDelete ? `${count}` : null,
    matcherLabel: isMultiDelete ? I18n.text('reporting-action-components.actions.delete.modal.matcherLabel') : null
  });
};
DeleteReportModal.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  reports: ImmutablePropTypes.set.isRequired,
  onDeleteReportSuccess: PropTypes.func,
  filteredReportCount: PropTypes.number,
  filters: PropTypes.object
};
export default DeleteReportModal;